import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getStoredUser } from '../../services/authService';
import { getUserOnboardings, deleteOnboarding } from '../../services/onboardingService';
import Card from '../../components/common/Card';
import Button from '../../components/forms/Button';
import './Dashboard.css';

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const storedUser = getStoredUser();
    // console.log('Stored User:', storedUser._id);
    setUser(storedUser);

    if (storedUser?._id) {
      fetchUserOnboardings(storedUser._id);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserOnboardings = async (userId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getUserOnboardings(userId);
      setHotels(response.data?.records || response.records || []);
    } catch (err) {
      console.error('Failed to fetch onboardings:', err);
      
      // More descriptive error messages
      let errorMessage = 'Failed to load your hotels';
      if (err.message.includes('<!DOCTYPE') || err.message.includes('Unexpected token')) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else if (err.message.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your connection.';
      } else {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (hotelId) => {
    if (!window.confirm('Are you sure you want to delete this hotel?')) {
      return;
    }

    try {
      await deleteOnboarding(hotelId);
      setHotels(hotels.filter(h => h._id !== hotelId));
      alert('Hotel deleted successfully');
    } catch (err) {
      console.error('Failed to delete hotel:', err);
      
      let errorMessage = 'Failed to delete hotel';
      if (err.message.includes('<!DOCTYPE') || err.message.includes('Unexpected token')) {
        errorMessage = 'Authentication failed. Please log in again.';
      } else {
        errorMessage = err.message;
      }
      
      alert(errorMessage);
    }
  };

  const stats = [
    {
      title: 'Total Hotels',
      value: hotels.length,
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
      ),
      color: 'primary'
    },
    {
      title: 'Active Hotels',
      value: hotels.filter((h) => h.status === true).length,
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
        </svg>
      ),
      color: 'success'
    },
    {
      title: 'Pending',
      value: hotels.filter((h) => h.status === false).length,
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10"></circle>
          <polyline points="12 6 12 12 16 14"></polyline>
        </svg>
      ),
      color: 'info'
    },
    {
      title: 'Avg Rating',
      value: hotels.length > 0
        ? (hotels.reduce((sum, h) => sum + (h.rating || 0), 0) / hotels.length).toFixed(1)
        : '0.0',
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
        </svg>
      ),
      color: 'warning'
    }
  ];

  return (
    <div className="dashboard-page">
      <div className="container">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Welcome back, {user?.username || user?.name}!</h1>
            <p className="dashboard-subtitle">Here's an overview of your hotels</p>
          </div>
          
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <Card key={index} className="stat-card">
              <div className="stat-content">
                <div className={`stat-icon stat-icon-${stat.color}`}>{stat.icon}</div>
                <div className="stat-details">
                  <p className="stat-title">{stat.title}</p>
                  <h3 className="stat-value">{stat.value}</h3>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Loading State */}
        {loading && (
          <Card className="hotels-card">
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>Loading your hotels...</p>
            </div>
          </Card>
        )}

        {/* Error State */}
        {error && !loading && (
          <Card className="hotels-card">
            <div style={{ textAlign: 'center', padding: '2rem', color: '#dc3545' }}>
              <p>Error: {error}</p>
              <Button
                variant="primary"
                onClick={() => user?._id && fetchUserOnboardings(user._id)}
                style={{ marginTop: '1rem' }}
              >
                Retry
              </Button>
            </div>
          </Card>
        )}

        {/* Hotels Table */}
        {!loading && !error && (
          <Card title="Your Hotels" className="hotels-card">
            {hotels.length > 0 ? (
              <div className="table-responsive">
                <table className="hotels-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Check In/Out</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hotels.map((hotel) => (
                      <tr key={hotel._id}>
                        <td className="font-weight-bold">
                          <div>
                            {hotel.name || 'N/A'}
                          </div>
                        </td>
                        <td>
                          <div>
                            {hotel.address?.city || 'N/A'}
                          </div>
                        </td>
                        <td>
                          <span className={`badge badge-${hotel.status ? 'success' : 'warning'}`}>
                            {hotel.status ? 'Approved' : 'Pending'}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontSize: '0.9em' }}>
                            <div>In: {hotel.check_in || 'N/A'}</div>
                            <div>Out: {hotel.check_out || 'N/A'}</div>
                          </div>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <Link to={`/Showhotelonboarding?show=${hotel._id}`}>
                              <button
                                className="btn-icon"
                                title="View"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                  <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                              </button>
                            </Link>
                            <Link to={`/hotel-onboarding?edit=${hotel._id}`}>
                              <button
                                className="btn-icon"
                                title="Edit"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                              </button>
                            </Link>
                            <Link to={`/room-management?hotel=${hotel._id}`}>
                              <button
                                className="btn-icon"
                                title="Manage Rooms"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                  <line x1="16" y1="2" x2="16" y2="6"></line>
                                  <line x1="8" y1="2" x2="8" y2="6"></line>
                                  <line x1="3" y1="10" x2="21" y2="10"></line>
                                </svg>
                              </button>
                            </Link>
                            <button
                              className="btn-icon btn-icon-danger"
                              title="Delete"
                              onClick={() => handleDelete(hotel._id)}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <p>No hotels registered yet.</p>
                <Link to="/hotel-onboarding">
                  <Button variant="primary">Add Your First Hotel</Button>
                </Link>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default Dashboard;